import { useEffect, useState } from '../../lib/teact/teact';

import { getTrueWebSocketEngine } from './TrueWebSocketEngine';
import { getWebSocketManager } from './WebSocketManager';

const RETRY_DELAY_MS = 30000;
const DEXSCREENER_ICON_URL = 'https://dd.dexscreener.com/ds-data/tokens';
const WORKER_URL = 'https://ape-data-proxy.hello-maturevats.workers.dev';
const GMGN_BASE_URL = 'https://openapi.gmgn.ai';
const GMGN_API_KEY = 'gmgn_solbscbaseethmonadtron';

// Chain ID mapping for DexScreener icon URLs
const CHAIN_ICON_MAP: Record<string, string> = {
  solana: 'solana',
  ethereum: 'ethereum',
  bsc: 'bsc',
  base: 'base',
  arbitrum: 'arbitrum',
  polygon: 'polygon',
  avalanche: 'avalanche',
  optimism: 'optimism',
  ton: 'ton',
  sui: 'sui',
  aptos: 'aptos',
  blast: 'blast',
  cronos: 'cronos',
  fantom: 'fantom',
  robinhood: 'robinhood',
};

const CHAIN_SLUG_MAP: Record<string, string> = {
  solana: 'sol',
  sol: 'sol',
  ethereum: 'eth',
  eth: 'eth',
  bsc: 'bsc',
  base: 'base',
  arbitrum: 'arb',
  arb: 'arb',
  robinhood: 'robinhood',
};

export interface TokenSafetyData {
  holders: number;
  top10Percent: number;
  devPercent: number;
  snipersCount: number;
  insidersPercent: number;
  bundlersPercent: number;
  lpBurnedPercent: number;
  proTraders: number;
  dexPaid: boolean;
  devAddress?: string;
  devCreatedCount?: number;
  devFundFrom?: string;
  devSoldAll?: boolean;
  devAthMcap?: number;
  tokenAthMcap?: number;
  isMintRenounced?: boolean;
  isFreezeRenounced?: boolean;
  buyTaxPct?: number;
  sellTaxPct?: number;
  isHoneypot?: boolean;
  whalesCount?: number;
  ratWallets?: number;
  smartWallets?: number;
}

export interface TokenDevPrediction {
  validTokens: number;
  avgPeakMcap: number;
  avgPeakMins: number;
  avgMcap?: number;
  avgTime?: number;
  targetHit?: boolean;
  strategy?: string;
  totalCreated?: number;
  openRatio?: number;
}

export interface CandidateInfo {
  mint: string;
  chain: string;
  name?: string;
  symbol?: string;
  marketCap?: number;
  imageUrl?: string;
  priceUsd?: number;
  priceChange24h?: number;
  volume24h?: number;
  liquidity?: number;
  createdAt?: number;
  poolAddress?: string;
}

export interface TokenData {
  priceUsd?: number;
  priceChange24h?: number;
  marketCap?: number;
  volume24h?: number;
  liquidity?: number;
  txns24h?: { buys: number; sells: number };
  createdAt?: number;
  poolAddress?: string;
  websites?: { label: string; url: string }[];
  socials?: { type: string; url: string }[];
  name?: string;
  symbol?: string;
  imageUrl?: string;
  chain?: string;
  mint?: string;
  isLoading?: boolean;
  isNotFound?: boolean;
  hasFetchError?: boolean;
  safetyData?: TokenSafetyData;
  isSafetyLoading?: boolean;
  devPrediction?: TokenDevPrediction;
  isDevPredictionLoading?: boolean;
  candidatePairs?: CandidateInfo[];
  hasBeenSwept?: boolean;
  fetchedAt?: number;
  isLiveWs?: boolean;
  lastWsTickAt?: number;
  bondingCurveProgress?: number;
  telegramCallCount?: number;
}

type TokenSubscriber = (data: TokenData) => void;

let gmgnBannedUntil = 0;
const gmgnRequestQueue: (() => void)[] = [];
let gmgnActiveRequests = 0;
const MAX_GMGN_CONCURRENT = 2; // Strict limit to prevent IP burst bans

export let getCachedToken: (key: string) => TokenData | undefined;

async function executeGmgnQueue<T>(task: () => Promise<T>): Promise<T | undefined> {
  if (Date.now() < gmgnBannedUntil) {
    return undefined; // IP is banned, fast-fail to worker proxy
  }

  if (gmgnActiveRequests >= MAX_GMGN_CONCURRENT) {
    await new Promise<void>((resolve) => gmgnRequestQueue.push(resolve));
  }

  if (Date.now() < gmgnBannedUntil) {
    return undefined; // Banned while waiting in queue
  }

  gmgnActiveRequests++;
  try {
    return await task();
  } finally {
    gmgnActiveRequests--;
    if (gmgnRequestQueue.length > 0) {
      const next = gmgnRequestQueue.shift();
      if (next) next();
    }
  }
}

// Direct GMGN fetch from browser (user's IP, not worker IP)
async function gmgnDirectFetch(path: string, params: Record<string, string | number | undefined> = {}): Promise<any> {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const clientId = crypto.randomUUID();
  const url = new URL(`${GMGN_BASE_URL}${path}`);

  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined) {
      url.searchParams.append(key, String(val));
    }
  }
  url.searchParams.append('timestamp', timestamp);
  url.searchParams.append('client_id', clientId);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  return executeGmgnQueue(async () => {
    // eslint-disable-next-line no-console
    console.log(`[APE] GMGN direct → ${path}`, Object.fromEntries(url.searchParams));

    try {
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'X-APIKEY': GMGN_API_KEY,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const text = await res.text();

    if (res.status === 429) {
      try {
        const json = JSON.parse(text);
        if (json.reset_at) {
          gmgnBannedUntil = json.reset_at * 1000;
          // eslint-disable-next-line no-console
          console.warn(`[APE] GMGN RATE-LIMITED (429) on ${path}. IP Banned until ${new Date(gmgnBannedUntil).toLocaleTimeString()}`);
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[APE] GMGN RATE-LIMITED (429) on ${path} — raw body:`, text);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[APE] GMGN RATE-LIMITED (429) on ${path} — raw body:`, text);
      }
      return undefined; // Rate-limited — caller will use worker fallback
    }

    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error(`[APE] GMGN HTTP ${res.status} on ${path} — raw body:`, text);
      return undefined;
    }

    try {
      const json = JSON.parse(text);
      if (json && json.code !== 0) {
        // eslint-disable-next-line no-console
        console.warn(`[APE] GMGN API error code=${json.code} on ${path}:`, json);
        return undefined;
      }
      if (json && (json.code === 0 || json.data)) {
        // eslint-disable-next-line no-console
        console.log(`[APE] GMGN OK ${path} → keys:`, Object.keys(json.data || json));
        return json.data || json;
      }
    } catch (parseErr) {
      // eslint-disable-next-line no-console
      console.error(`[APE] GMGN JSON parse fail on ${path}:`, parseErr, 'raw:', text.slice(0, 300));
    }
    return undefined;
  } catch (err) {
    clearTimeout(timeoutId);
    // eslint-disable-next-line no-console
    console.error(`[APE] GMGN fetch error on ${path}:`, err);
    return undefined;
  }
});
}

// Helper to fetch from GeckoTerminal API
async function geckoterminalDirectFetch(chainSlug: string, poolAddress: string, timeframe: 'minute' | 'hour' | 'day', limit: number, beforeTimestamp: number): Promise<any> {
  // Convert sol/eth/bsc/base to gecko chain names
  let geckoChain = chainSlug;
  if (chainSlug === 'sol') geckoChain = 'solana';
  else if (chainSlug === 'eth') geckoChain = 'eth';
  else if (chainSlug === 'bsc') geckoChain = 'bsc';
  else if (chainSlug === 'robinhood') geckoChain = 'robinhood';
  
  const url = `https://api.geckoterminal.com/api/v2/networks/${geckoChain}/pools/${poolAddress}/ohlcv/${timeframe}?limit=${limit}&before_timestamp=${beforeTimestamp}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      console.error(`[APE] GeckoTerminal HTTP ${res.status} on ${url}`);
      return undefined;
    }
    
    const json = await res.json();
    return json?.data?.attributes?.ohlcv_list;
  } catch (err) {
    clearTimeout(timeoutId);
    console.error(`[APE] GeckoTerminal fetch error:`, err);
    return undefined;
  }
}

function parsePercent(val: string | number | undefined): number {
  if (val === undefined || val === '') return 0;
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return 0;
  return num <= 1 ? parseFloat((num * 100).toFixed(1)) : parseFloat(num.toFixed(1));
}

// Parse GMGN info + security responses into TokenSafetyData
function parseSafetyFromGmgn(info: any, sec: any): TokenSafetyData | undefined {
  const hasValidData = Boolean(
    info && (info.holder_count || info.stat?.holder_count || info.total_supply || sec?.renounced_mint !== undefined),
  );
  if (!hasValidData) return undefined;

  const stat = info?.stat || {};
  const dev = info?.dev || {};
  const walletTags = info?.wallet_tags_stat || {};

  const rawTop10 = sec?.top_10_holder_rate ?? stat.top_10_holder_rate ?? info?.top_10_holder_rate;
  const top10Percent = parsePercent(rawTop10);

  let devPercent = 0;
  const rawDevHold = stat.dev_team_hold_rate ?? stat.creator_hold_rate ?? info?.dev_team_hold_rate;
  if (rawDevHold !== undefined) {
    devPercent = parsePercent(rawDevHold);
  } else if (dev.creator_token_balance && info?.total_supply) {
    const bal = parseFloat(dev.creator_token_balance) || 0;
    const sup = parseFloat(info.total_supply) || 0;
    if (sup > 0) devPercent = parseFloat(((bal / sup) * 100).toFixed(1));
  }

  const snipersCount = walletTags.sniper_wallets
    ?? stat.bot_degen_count
    ?? stat.sniper_count
    ?? info?.sniper_count
    ?? 0;
  const rawInsiders = stat.top_entrapment_trader_percentage ?? stat.insider_rate ?? info?.insider_rate;
  const insidersPercent = parsePercent(rawInsiders);
  const bundlersPercent = parsePercent(stat.top_bundler_trader_percentage ?? stat.bundler_rate ?? info?.bundler_rate);

  let lpBurnedPercent = 0;
  const isLpBurned = sec?.burn_ratio === '1'
    || sec?.burn_status === 'burn'
    || sec?.lock_summary?.lock_detail?.[0]?.is_blackhole
    || sec?.is_lp_burned;
  if (isLpBurned) {
    lpBurnedPercent = 100;
  } else if (sec?.lp_locked_pct !== undefined || sec?.burn_ratio !== undefined) {
    lpBurnedPercent = parsePercent(sec.lp_locked_pct ?? sec.burn_ratio);
  }

  const holders = info?.holder_count || stat.holder_count || 0;
  const proTraders = walletTags.smart_wallets
    ?? walletTags.renowned_wallets
    ?? stat.smart_degen_count
    ?? stat.signal_count
    ?? 0;

  const dexPaid = Boolean(
    dev.dexscr_update_link
    || dev.dexscr_boost_fee
    || info?.link?.dexscreener_paid
    || info?.dex_paid
    || (info?.link?.twitter_username && info?.link?.website),
  );

  const devAddress = dev.creator_address || info?.creator || undefined;
  const devCreatedCount = Math.max(
    dev.creator_open_count || 0,
    stat.creator_created_count || 0,
    dev.twitter_create_token_count || 0,
    dev.creator_token_count || 0,
    1,
  );
  const devFundFrom = dev.fund_from || undefined;
  const devSoldAll = dev.creator_token_status === 'creator_close' || stat.creator_token_balance === '0';
  const devAthMcap = dev.ath_token_info?.ath_mc ? parseFloat(dev.ath_token_info.ath_mc) : undefined;

  const tokenAthMcap = info?.ath_price && info?.total_supply
    ? parseFloat(info.ath_price) * parseFloat(info.total_supply)
    : undefined;

  const isMintRenounced = Boolean(sec?.renounced_mint);
  const isFreezeRenounced = Boolean(sec?.renounced_freeze_account);
  const buyTaxPct = sec?.buy_tax !== undefined ? parseFloat((parseFloat(sec.buy_tax) * 100).toFixed(1)) : 0;
  const sellTaxPct = sec?.sell_tax !== undefined ? parseFloat((parseFloat(sec.sell_tax) * 100).toFixed(1)) : 0;
  const isHoneypot = sec?.is_honeypot === 1 || sec?.is_honeypot === 'yes' || false;

  const whalesCount = walletTags.whale_wallets || info?.bluechip_owner_count || 0;
  const ratWallets = walletTags.rat_trader_wallets || 0;
  const smartWallets = walletTags.smart_wallets || stat.signal_count || 0;

  return {
    holders,
    top10Percent,
    devPercent,
    snipersCount,
    insidersPercent,
    bundlersPercent,
    lpBurnedPercent,
    proTraders,
    dexPaid,
    devAddress,
    devCreatedCount,
    devFundFrom,
    devSoldAll,
    devAthMcap,
    tokenAthMcap,
    isMintRenounced,
    isFreezeRenounced,
    buyTaxPct,
    sellTaxPct,
    isHoneypot,
    whalesCount,
    ratWallets,
    smartWallets,
  };
}

// Parse GMGN created_tokens into TokenDevPrediction using wrapper bot logic
async function computeDevPrediction(chainSlug: string, devAddress: string): Promise<TokenDevPrediction | undefined> {
  const created = await gmgnDirectFetch('/v1/user/created_tokens', {
    chain: chainSlug,
    wallet_address: devAddress,
    limit: 10,
  });
  if (!created) return undefined;

  const rawData = created?.data || created;
  const tokens: any[] = Array.isArray(rawData)
    ? rawData
    : (rawData?.tokens || []);

  const totalCreated = (rawData?.inner_count || 0) + (rawData?.open_count || 0) || tokens.length;
  const openRatio = rawData?.open_ratio ? parseFloat(rawData.open_ratio) : undefined;

  if (tokens.length === 0) {
    return {
      validTokens: 1,
      avgPeakMcap: 0,
      avgPeakMins: 30,
      avgMcap: 0,
      avgTime: 1800000,
      strategy: 'Fresh developer profile',
      totalCreated: 1,
    };
  }

  if (tokens.length === 1) {
    const t = tokens[0];
    const tokenAthMcap = parseFloat(t.token_ath_mc || t.market_cap || rawData?.creator_ath_info?.ath_mc || '0');
    const isGraduated = Boolean(t.is_open);
    const avgPeakMins = isGraduated ? (tokenAthMcap > 500000 ? 90 : 35) : (tokenAthMcap > 60000 ? 15 : 5);
    return {
      validTokens: 1,
      avgPeakMcap: tokenAthMcap,
      avgPeakMins,
      avgMcap: tokenAthMcap,
      avgTime: avgPeakMins * 60000,
      strategy: isGraduated ? 'Graduated developer profile' : 'First coin / Fresh developer profile',
      totalCreated: 1,
      openRatio: isGraduated ? 1 : 0,
    };
  }

  let totalAthTime = 0;
  let totalAthMcap = 0;
  let validTokens = 0;

  for (const t of tokens.slice(0, 10)) {
    const tokenPeakMcap = parseFloat(t.token_ath_mc || t.market_cap || '0');
    // If token is graduated (is_open), dev sustained pump to DEX (average 35-90 min peak window).
    // If token stayed on curve, typical dev exit/peak occurs within 4-8 minutes.
    let timeToAthMins = 5;
    if (t.is_open) {
      timeToAthMins = tokenPeakMcap > 500000 ? 90 : 35;
    } else if (tokenPeakMcap > 60000) {
      timeToAthMins = 18;
    }

    totalAthTime += timeToAthMins * 60000;
    totalAthMcap += tokenPeakMcap;
    validTokens++;
  }

  const avgTime = validTokens > 0 ? totalAthTime / validTokens : 1800000;
  const avgMcap = validTokens > 0 ? totalAthMcap / validTokens : 0;
  const avgPeakMins = Math.max(1, Math.round(avgTime / 60000));
  const avgPeakMcap = avgMcap;

  let strategy = 'Short-cycle: typical exit on initial run';
  if (avgPeakMins <= 5 && avgPeakMcap < 35000) {
    strategy = 'Ultra-fast P&D: exits quickly';
  } else if (avgPeakMins >= 60 || avgPeakMcap >= 500000) {
    strategy = 'Proven builder: holds for higher peak';
  } else if (openRatio !== undefined && openRatio >= 0.5) {
    strategy = 'High graduation rate: aims for DEX';
  }

  return {
    validTokens,
    avgPeakMcap,
    avgPeakMins,
    avgMcap,
    avgTime,
    strategy,
    totalCreated,
    openRatio,
  };
}

export interface HistoricalCandleMetrics {
  startPrice?: number;
  maxPrice?: number;
  minPrice?: number;
  volume?: number;
}

const pendingCandleRequests = new Map<string, Promise<HistoricalCandleMetrics>>();

export async function fetchHistoricalCandles(
  address: string,
  chain: string = 'sol',
  messageDateMs?: number,
  currentPrice?: number,
): Promise<HistoricalCandleMetrics> {
  if (!messageDateMs || Date.now() - messageDateMs < 300000) {
    return {
      startPrice: currentPrice,
      maxPrice: currentPrice,
      minPrice: currentPrice,
      volume: 0,
    };
  }

  const cacheKey = `${address}_${messageDateMs}`;
  if (pendingCandleRequests.has(cacheKey)) {
    return pendingCandleRequests.get(cacheKey)!;
  }

  const promise = (async () => {
    const tokenInfo = getCachedToken(address);
    const realChain = tokenInfo?.chain || chain || (address.startsWith('0x') ? 'ethereum' : 'solana');
    
    const chainSlug = (realChain === 'solana' || realChain === 'sol') ? 'sol'
      : (realChain === 'bsc' || realChain === 'binance') ? 'bsc'
        : (realChain === 'ethereum' || realChain === 'eth') ? 'eth'
          : (realChain === 'base') ? 'base'
            : (realChain === 'robinhood') ? 'robinhood'
              : 'sol';

    const ageMs = Date.now() - messageDateMs;
    let resolution = '1m';
    if (ageMs > 7 * 86400 * 1000) {
      resolution = '1h';
    } else if (ageMs > 86400 * 1000) {
      resolution = '15m';
    } else if (ageMs > 6 * 3600 * 1000) {
      resolution = '5m';
    }

    const messageDateSec = Math.floor(messageDateMs / 1000);
    // Step A: Sniper Entry (1-minute resolution, exact 10 minute window around the message)
    const sniperFromSec = Math.max(0, messageDateSec - 300);
    const sniperToSec = messageDateSec + 300;

    // Step B: Zoomed ATH (dynamic resolution, from message date to now)
    const zoomedFromSec = sniperFromSec;

    try {
      const [sniperKline, zoomedKline] = await Promise.all([
        gmgnDirectFetch('/v1/market/token_kline', {
          chain: chainSlug,
          address,
          resolution: '1m', // Force maximum resolution for entry price
          from: sniperFromSec,
          to: sniperToSec,
        }).catch(() => null),
        gmgnDirectFetch('/v1/market/token_kline', {
          chain: chainSlug,
          address,
          resolution, // Coarse resolution for long-term ATH
          from: zoomedFromSec,
        }).catch(() => null),
      ]);

      let sniperCandles: any[] = sniperKline?.data || sniperKline?.list || (Array.isArray(sniperKline) ? sniperKline : []);
      let zoomedCandles: any[] = zoomedKline?.data || zoomedKline?.list || (Array.isArray(zoomedKline) ? zoomedKline : []);

      // If GMGN completely fails or returns empty data, Fallback to GeckoTerminal
      if ((!sniperCandles.length && !zoomedCandles.length) || gmgnBannedUntil > Date.now()) {
        const tokenInfo = getCachedToken(address);
        if (tokenInfo?.poolAddress) {
          // eslint-disable-next-line no-console
          console.warn(`[APE] GMGN historical fetch failed/empty for ${address}. Falling back to GeckoTerminal OHLCV using pool: ${tokenInfo.poolAddress}`);
          const nowSec = Math.floor(Date.now() / 1000);
          const geckoCandles = await geckoterminalDirectFetch(chainSlug, tokenInfo.poolAddress, 'hour', Math.max(2, Math.ceil((nowSec - sniperFromSec) / 3600)), nowSec);
          if (geckoCandles && geckoCandles.length > 0) {
            // Map GeckoTerminal format: [timestamp, open, high, low, close, volume] to GMGN format {time, open, high, low, close, volume}
            zoomedCandles = geckoCandles.map((c: number[]) => ({
              time: c[0] * 1000,
              open: c[1],
              high: c[2],
              low: c[3],
              close: c[4],
              volume: c[5]
            }));
            sniperCandles = zoomedCandles; // Use zoomed for both since we fetched hours
          }
        }
      }

      if (!sniperCandles.length && !zoomedCandles.length) {
        return { startPrice: undefined, maxPrice: undefined, minPrice: undefined, volume: 0 };
      }

      // Calculate highly-accurate Entry Price using sniper candles
      let closestCandle = sniperCandles[0] || zoomedCandles[0];
      let minDiff = Infinity;
      
      for (const c of sniperCandles) {
        const rawTime = Number(c.time || c.t || c.timestamp || 0);
        const candleTimeMs = rawTime < 1e11 ? rawTime * 1000 : rawTime;
        const diff = Math.abs(candleTimeMs - messageDateMs);
        if (diff < minDiff) {
          minDiff = diff;
          closestCandle = c;
        }
      }

      // If sniper failed, fallback to zoomed for entry price
      if (sniperCandles.length === 0) {
        for (const c of zoomedCandles) {
          const rawTime = Number(c.time || c.t || c.timestamp || 0);
          const candleTimeMs = rawTime < 1e11 ? rawTime * 1000 : rawTime;
          const diff = Math.abs(candleTimeMs - messageDateMs);
          if (diff < minDiff) {
            minDiff = diff;
            closestCandle = c;
          }
        }
      }

      // Calculate mathematical ATH using zoomed candles (since they cover the whole timeline)
      let maxHigh = 0;
      let minLow = Infinity;
      let totalVol = 0;
      
      const allCandles = zoomedCandles.length > 0 ? zoomedCandles : sniperCandles;
      for (const c of allCandles) {
        const high = parseFloat(c.high || c.h || c.close || c.c || '0');
        const low = parseFloat(c.low || c.l || c.close || c.c || '0');
        const vol = parseFloat(c.volume || c.v || c.vol || '0');

        if (high > maxHigh) maxHigh = high;
        if (low > 0 && low < minLow) minLow = low;
        totalVol += vol;
      }

      const startPriceStr = closestCandle?.open || closestCandle?.o || closestCandle?.close || closestCandle?.c;
      const startPrice = startPriceStr ? parseFloat(startPriceStr) : undefined;
      const maxPrice = maxHigh > 0 ? maxHigh : startPrice;
      const minPrice = minLow < Infinity ? minLow : startPrice;

      return {
        startPrice: startPrice,
        maxPrice: maxPrice,
        minPrice: minPrice,
        volume: totalVol,
      };
    } catch (err) {
      console.error(`[APE] fetchHistoricalCandles failed for ${address}:`, err);
      return { startPrice: undefined, maxPrice: undefined, minPrice: undefined, volume: 0 };
    }
  })();

  pendingCandleRequests.set(cacheKey, promise);
  
  // Clear the memory cache after 60 seconds (enough to protect against stampedes but allow fresh fetches later)
  promise.finally(() => {
    setTimeout(() => {
      pendingCandleRequests.delete(cacheKey);
    }, 60000);
  });

  return promise;
}


export type FetchPriority = 'CRITICAL' | 'VISIBLE' | 'BACKGROUND';

export interface ApeLogEntry {
  timestamp: string;
  tokenKey: string;
  route: 'DIRECT_CLIENT' | 'WORKER_PROXY' | 'GMGN_FALLBACK';
  status: 'SUCCESS' | 'RATE_LIMITED_429' | 'ERROR' | 'NOT_FOUND';
  httpStatus?: number;
  durationMs: number;
  priority: FetchPriority;
  activeCount?: number;
  pendingCount?: number;
  pair?: string;
  mcap?: string;
  price?: string;
  liquidity?: string;
  fallbackTriggered?: string;
  detail?: string;
}

const MAX_LOG_HISTORY = 200;
const apeLogs: ApeLogEntry[] = [];

function formatRouteName(route: 'DIRECT_CLIENT' | 'WORKER_PROXY' | 'GMGN_FALLBACK') {
  if (route === 'DIRECT_CLIENT') return '[1] Direct Client IP (api.dexscreener.com)';
  if (route === 'WORKER_PROXY') return '[2] Cloudflare Worker Proxy';
  return '[3] GMGN OpenAPI Fallback';
}

function recordApeLog(entry: ApeLogEntry) {
  if (apeLogs.length >= MAX_LOG_HISTORY) {
    apeLogs.shift();
  }
  apeLogs.push(entry);

  const active = entry.activeCount ?? 0;
  const pending = entry.pendingCount ?? 0;

  if (entry.status === 'SUCCESS') {
    const liqPart = entry.liquidity ? ` | Liquidity: ${entry.liquidity}` : '';
    // eslint-disable-next-line no-console
    console.log(
      `✅ [APE] Fetch Success | Token: ${entry.tokenKey}\n`
      + `├─ Route: ${formatRouteName(entry.route)}\n`
      + `├─ Pair: ${entry.pair || entry.tokenKey}\n`
      + `├─ MCAP: ${entry.mcap || 'N/A'} | Price: ${entry.price || 'N/A'}${liqPart}\n`
      + `├─ Latency: ${entry.durationMs}ms\n`
      + `└─ Queue State: ${active} active / ${pending} pending (Priority: ${entry.priority})`,
    );
  } else if (entry.status === 'RATE_LIMITED_429') {
    // eslint-disable-next-line no-console
    console.warn(
      `🚨 [APE] Fetch Rate-Limited | Token: ${entry.tokenKey}\n`
      + `├─ Cause: HTTP 429 (Rate Limited / Too Many Requests)\n`
      + `├─ Route Attempted: ${formatRouteName(entry.route)}\n`
      + (entry.fallbackTriggered ? `├─ Fallback Triggered: ${entry.fallbackTriggered}\n` : '')
      + `├─ Latency: ${entry.durationMs}ms\n`
      + `├─ Queue State: ${active} active / ${pending} pending (Priority: ${entry.priority})\n`
      + `└─ Details: ${entry.detail || 'IP rate-limit reached, switching route'}`,
    );
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      `🚨 [APE] Fetch Failed | Token: ${entry.tokenKey}\n`
      + `├─ Cause: ${entry.status === 'NOT_FOUND' ? 'Zero Pairs Found' : 'Network / API Error'}\n`
      + `├─ Route Attempted: ${formatRouteName(entry.route)}\n`
      + (entry.fallbackTriggered ? `├─ Fallback Triggered: ${entry.fallbackTriggered}\n` : '')
      + `├─ Latency: ${entry.durationMs}ms\n`
      + `├─ Queue State: ${active} active / ${pending} pending (Priority: ${entry.priority})\n`
      + `└─ Details: ${entry.detail || 'Request failed'}`,
    );
  }
}

// Expose on window for easy inspection via Eruda
if (typeof window !== 'undefined') {
  (window as any).__APE_LOGS__ = apeLogs;
  (window as any).getApeLogs = () => {
    // eslint-disable-next-line no-console
    console.table(apeLogs);
    return apeLogs;
  };
  (window as any).copyApeLogs = () => {
    const json = JSON.stringify(apeLogs, undefined, 2);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(json);
      // eslint-disable-next-line no-console
      console.log(`[APE] Copied ${apeLogs.length} logs to clipboard!`);
    }
    return json;
  };
}

interface FetchQueueItem {
  tokenKey: string;
  priority: FetchPriority;
  resolve: () => void;
  enqueuedAt: number;
}

const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

class CryptoDataStore {
  private cache = new Map<string, TokenData>();
  private subscribers = new Map<string, Set<TokenSubscriber>>();
  private pendingRequests = new Map<string, Promise<void>>();
  private pendingSafetyRequests = new Map<string, Promise<void>>();
  private pendingPredictionRequests = new Map<string, Promise<void>>();

  private fetchQueue: FetchQueueItem[] = [];
  private activeFetches = 0;
  private readonly MAX_CONCURRENT_FETCHES = 6;
  private readonly MAX_VIP_CONCURRENT = 8;
  private lastBackgroundDispatchTime = 0;

  // RAF-based notification batch. All subscriber calls are queued here and
  // flushed once per animation frame to prevent WebSocket ticks from
  // triggering dozens of synchronous setState calls in the same millisecond.
  private pendingNotifications = new Map<TokenSubscriber, TokenData>();
  private rafHandle: number | undefined;

  subscribe(tokenKey: string, callback: TokenSubscriber, priority: FetchPriority = 'VISIBLE') {
    if (!this.subscribers.has(tokenKey)) {
      this.subscribers.set(tokenKey, new Set());
    }
    this.subscribers.get(tokenKey)!.add(callback);

    // Initial emit
    if (this.cache.has(tokenKey)) {
      callback(this.cache.get(tokenKey)!);
      // For ticker-based keys (e.g. $CAT), re-fetch if data is stale to handle cross-chat usage
      const cached = this.cache.get(tokenKey)!;
      if (tokenKey.startsWith('$') && cached.fetchedAt && Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
        this.pendingRequests.delete(tokenKey);
        this.fetchTokenData(tokenKey, priority);
      }
    } else {
      callback({ isLoading: true });
      this.fetchTokenData(tokenKey, priority);
    }

    return () => {
      const subs = this.subscribers.get(tokenKey);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          this.subscribers.delete(tokenKey);
        }
      }
    };
  }

  updateToken(tokenKey: string, partialData: Partial<TokenData>) {
    const current = this.cache.get(tokenKey) || {};
    const isLoading = partialData.isLoading !== undefined ? partialData.isLoading : (current.isLoading ?? false);
    const updated = { ...current, ...partialData, isLoading };
    this.cache.set(tokenKey, updated);

    // Mirror the data to the mint address key if it exists and is different from the tokenKey.
    if (updated.mint && tokenKey !== updated.mint) {
      const currentMint = this.cache.get(updated.mint) || {};
      const updatedMint = {
        ...currentMint,
        ...updated,
        safetyData: updated.safetyData || currentMint.safetyData,
        isSafetyLoading: updated.safetyData ? false : currentMint.isSafetyLoading,
        devPrediction: updated.devPrediction || currentMint.devPrediction,
        isDevPredictionLoading: updated.devPrediction ? false : currentMint.isDevPredictionLoading,
      };
      this.cache.set(updated.mint, updatedMint);

      const mintSubs = this.subscribers.get(updated.mint);
      if (mintSubs) {
        mintSubs.forEach((cb) => this.scheduleNotification(cb, updatedMint));
      }
    }

    // Reverse-mirror: If tokenKey is a mint address, propagate updates to any matching symbol entries (e.g. $CAT)
    if (!tokenKey.startsWith('$')) {
      this.cache.forEach((cachedData, key) => {
        if (key.startsWith('$') && cachedData.mint === tokenKey) {
          const updatedSymbol = {
            ...cachedData,
            ...partialData,
            isLoading: partialData.isLoading !== undefined ? partialData.isLoading : (cachedData.isLoading ?? false),
          };
          this.cache.set(key, updatedSymbol);
          const symbolSubs = this.subscribers.get(key);
          if (symbolSubs) {
            symbolSubs.forEach((cb) => this.scheduleNotification(cb, updatedSymbol));
          }
        }
      });
    }

    const subs = this.subscribers.get(tokenKey);
    if (subs) {
      subs.forEach((cb) => this.scheduleNotification(cb, updated));
    }
  }

  // Queue a subscriber notification to fire on the next animation frame.
  // If the same subscriber gets updated multiple times before the RAF fires,
  // only the latest data wins — collapsing N rapid WS ticks into 1 render.
  private scheduleNotification(cb: TokenSubscriber, data: TokenData) {
    this.pendingNotifications.set(cb, data);
    if (this.rafHandle === undefined) {
      this.rafHandle = requestAnimationFrame(() => {
        this.rafHandle = undefined;
        const batch = this.pendingNotifications;
        this.pendingNotifications = new Map();
        batch.forEach((tokenData, subscriber) => subscriber(tokenData));
      });
    }
  }

  // Method used by WebSocket manager to bulk-update by symbol (e.g. from Binance)
  updateTokenBySymbol(symbol: string, partialData: Partial<TokenData>) {
    this.cache.forEach((data, key) => {
      if (data.symbol === symbol || key === `$${symbol}`) {
        this.updateToken(key, partialData);
      }
    });
  }

  // Returns all currently subscribed tokens (useful for polling batch prices)
  getActiveTokens() {
    const active: { chain: string; address: string; key: string }[] = [];
    this.subscribers.forEach((subs, key) => {
      if (subs.size > 0) {
        const data = this.cache.get(key);
        if (data && data.chain && data.mint) {
          active.push({ chain: data.chain, address: data.mint, key });
        }
      }
    });
    return active;
  }

  public getTokenData(tokenKey: string): TokenData {
    return this.cache.get(tokenKey) || {};
  }

  public promoteToLiveWebSocket(tokenKey: string, chain?: string, mint?: string) {
    const current = this.cache.get(tokenKey) || {};
    const finalChain = chain || current.chain;
    const finalMint = mint || current.mint || (!tokenKey.startsWith('$') ? tokenKey : undefined);

    this.updateToken(tokenKey, {
      isLiveWs: true,
      lastWsTickAt: Date.now(),
    });

    if (finalMint && finalChain) {
      getTrueWebSocketEngine().subscribeToToken(finalMint, finalChain);
    }
  }

  // Bypasses the cache and pending requests to force a fresh VIP fetch
  public forceFetchTokenData(tokenKey: string) {
    if (this.pendingRequests.has(tokenKey)) {
      this.pendingRequests.delete(tokenKey);
    }
    this.updateToken(tokenKey, { isLoading: true });
    return this.fetchTokenData(tokenKey, 'CRITICAL');
  }

  public async fetchTokenData(tokenKey: string, priority: FetchPriority = 'VISIBLE') {
    if (this.pendingRequests.has(tokenKey)) {
      // If a VIP critical request comes in for an already enqueued item, upgrade it to front
      if (priority === 'CRITICAL') {
        const index = this.fetchQueue.findIndex((item) => item.tokenKey === tokenKey);
        if (index > 0) {
          const [promoted] = this.fetchQueue.splice(index, 1);
          promoted.priority = 'CRITICAL';
          this.fetchQueue.unshift(promoted);
          this.processQueue();
        }
      }
      return this.pendingRequests.get(tokenKey);
    }

    const promise = new Promise<void>((resolve) => {
      const item: FetchQueueItem = {
        tokenKey,
        priority,
        resolve,
        enqueuedAt: Date.now(),
      };

      if (priority === 'CRITICAL') {
        this.fetchQueue.unshift(item); // VIP: Head of line
      } else if (priority === 'VISIBLE') {
        // Insert before background items
        const firstBgIndex = this.fetchQueue.findIndex((i) => i.priority === 'BACKGROUND');
        if (firstBgIndex !== -1) {
          this.fetchQueue.splice(firstBgIndex, 0, item);
        } else {
          this.fetchQueue.push(item);
        }
      } else {
        this.fetchQueue.push(item);
      }

      this.processQueue();
    });

    this.pendingRequests.set(tokenKey, promise);
    return promise;
  }

  private async processQueue() {
    if (this.fetchQueue.length === 0) {
      return;
    }

    const nextItem = this.fetchQueue[0];
    const isVip = nextItem.priority === 'CRITICAL';
    const maxAllowed = isVip ? this.MAX_VIP_CONCURRENT : this.MAX_CONCURRENT_FETCHES;

    if (this.activeFetches >= maxAllowed) {
      return;
    }

    // Pacing delay for background requests (prevents rate spikes during fast scroll)
    if (!isVip && nextItem.priority === 'BACKGROUND') {
      const elapsed = Date.now() - this.lastBackgroundDispatchTime;
      if (elapsed < 150) {
        setTimeout(() => this.processQueue(), 150 - elapsed);
        return;
      }
      this.lastBackgroundDispatchTime = Date.now();
    }

    this.activeFetches++;
    const { tokenKey, priority, resolve } = this.fetchQueue.shift()!;

    try {
      await this.performFetch(tokenKey, priority);
    } finally {
      this.pendingRequests.delete(tokenKey);
      this.activeFetches--;
      resolve();
      this.processQueue();
    }
  }

  private async performFetch(tokenKey: string, priority: FetchPriority = 'VISIBLE', retries = 1): Promise<void> {
    const fetchStartTime = Date.now();
    const query = tokenKey.startsWith('$') ? tokenKey.substring(1) : tokenKey;
    const isContractAddress = tokenKey.startsWith('0x') || tokenKey.endsWith('pump') || tokenKey.length >= 32;

    let success = false;

    // ═══════════════════════════════════════════════════════════════════════
    // TIER 1: Direct Client-Side DexScreener Fetch (User's Personal IP)
    // ═══════════════════════════════════════════════════════════════════════
    try {
      let isSearchFallback = false;
      let directUrl = isContractAddress
        ? `https://api.dexscreener.com/latest/dex/tokens/${encodeURIComponent(query)}`
        : `https://api.dexscreener.com/latest/dex/search?q=${encodeURIComponent(query)}`;

      let controller = new AbortController();
      let timeoutId = setTimeout(() => controller.abort(), 10000); // Increased to 10s

      let res = await fetch(directUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      // If it's a contract address and the tokens endpoint returns null pairs, 
      // it might be indexed only in search due to API lag. Fallback to search once.
      if (res.ok && isContractAddress) {
        const clone = res.clone();
        try {
           const preCheck = await clone.json();
           if (preCheck.pairs === null) {
              isSearchFallback = true;
              directUrl = `https://api.dexscreener.com/latest/dex/search?q=${encodeURIComponent(query)}`;
              controller = new AbortController();
              timeoutId = setTimeout(() => controller.abort(), 10000);
              res = await fetch(directUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                signal: controller.signal,
              });
              clearTimeout(timeoutId);
           }
        } catch (e) {}
      }

      const durationMs = Date.now() - fetchStartTime;

      if (res.status === 429) {
        recordApeLog({
          timestamp: new Date().toISOString(),
          tokenKey,
          route: 'DIRECT_CLIENT',
          status: 'RATE_LIMITED_429',
          httpStatus: 429,
          durationMs,
          priority,
          activeCount: this.activeFetches,
          pendingCount: this.fetchQueue.length,
          fallbackTriggered: '[2] Cloudflare Worker Proxy',
          detail: 'Direct client hit DexScreener 429 rate limit — switching to Worker Proxy',
        });
      } else if (res.ok) {
        const data = await res.json();
        if (data.pairs && data.pairs.length > 0) {
          this.applyPairData(tokenKey, data, 'DIRECT_CLIENT', durationMs, priority);
          success = true;
          return;
        } else {
          this.updateToken(tokenKey, { isNotFound: true, isLoading: false });
          recordApeLog({
            timestamp: new Date().toISOString(),
            tokenKey,
            route: 'DIRECT_CLIENT',
            status: 'NOT_FOUND',
            httpStatus: 200,
            durationMs,
            priority,
            activeCount: this.activeFetches,
            pendingCount: this.fetchQueue.length,
            detail: 'Zero pairs returned by DexScreener',
          });
          return;
        }
      }
    } catch (directErr: any) {
      const durationMs = Date.now() - fetchStartTime;
      recordApeLog({
        timestamp: new Date().toISOString(),
        tokenKey,
        route: 'DIRECT_CLIENT',
        status: 'ERROR',
        durationMs,
        priority,
        activeCount: this.activeFetches,
        pendingCount: this.fetchQueue.length,
        fallbackTriggered: '[2] Cloudflare Worker Proxy',
        detail: `Direct client fetch failed: ${directErr?.message || String(directErr)}`,
      });
    }

    if (success) return;

    // ═══════════════════════════════════════════════════════════════════════
    // TIER 2: Cloudflare Worker Proxy Fallback
    // ═══════════════════════════════════════════════════════════════════════
    try {
      const workerStartTime = Date.now();
      const res = await fetch(`${WORKER_URL}/api/token?q=${encodeURIComponent(query)}`);
      const durationMs = Date.now() - workerStartTime;

      if (res.ok) {
        const data = await res.json();
        if (!data.error && data.pairs && data.pairs.length > 0) {
          this.applyPairData(tokenKey, data, 'WORKER_PROXY', durationMs, priority);
          success = true;
          return;
        }
      }

      recordApeLog({
        timestamp: new Date().toISOString(),
        tokenKey,
        route: 'WORKER_PROXY',
        status: res.status === 429 ? 'RATE_LIMITED_429' : 'ERROR',
        httpStatus: res.status,
        durationMs,
        priority,
        activeCount: this.activeFetches,
        pendingCount: this.fetchQueue.length,
        fallbackTriggered: isContractAddress ? '[3] GMGN OpenAPI Fallback' : undefined,
        detail: `Worker proxy returned HTTP ${res.status}`,
      });
    } catch (workerErr: any) {
      recordApeLog({
        timestamp: new Date().toISOString(),
        tokenKey,
        route: 'WORKER_PROXY',
        status: 'ERROR',
        durationMs: Date.now() - fetchStartTime,
        priority,
        activeCount: this.activeFetches,
        pendingCount: this.fetchQueue.length,
        fallbackTriggered: isContractAddress ? '[3] GMGN OpenAPI Fallback' : undefined,
        detail: `Worker proxy connection error: ${workerErr?.message || String(workerErr)}`,
      });
    }

    if (success) return;

    // ═══════════════════════════════════════════════════════════════════════
    // TIER 3: GMGN OpenAPI Fallback (For Contract Addresses)
    // ═══════════════════════════════════════════════════════════════════════
    if (isContractAddress) {
      try {
        const gmgnStartTime = Date.now();
        const data = this.cache.get(tokenKey);
        const chain = data?.chain || (query.startsWith('0x') ? 'ethereum' : 'solana');
        const chainSlug = CHAIN_SLUG_MAP[chain] || (query.startsWith('0x') ? 'eth' : 'sol');
        const gmgnData = await gmgnDirectFetch('/v1/token/info', { chain: chainSlug, address: query });
        const durationMs = Date.now() - gmgnStartTime;

        if (gmgnData && (gmgnData.price?.price || gmgnData.symbol)) {
          this.applyGmgnData(tokenKey, gmgnData, chainSlug, durationMs, priority);
          success = true;
          return;
        }
      } catch (gmgnErr: any) {
        recordApeLog({
          timestamp: new Date().toISOString(),
          tokenKey,
          route: 'GMGN_FALLBACK',
          status: 'ERROR',
          durationMs: Date.now() - fetchStartTime,
          priority,
          activeCount: this.activeFetches,
          pendingCount: this.fetchQueue.length,
          detail: `GMGN fallback failed: ${gmgnErr?.message || String(gmgnErr)}`,
        });
      }
    }

    // All tiers exhausted
    const existing = this.cache.get(tokenKey);
    if (!existing || existing.isLoading) {
      this.updateToken(tokenKey, { hasFetchError: true, isLoading: true });
    }
    this.scheduleRetry(tokenKey, priority);
  }

  private applyPairData(
    tokenKey: string,
    data: any,
    route: 'DIRECT_CLIENT' | 'WORKER_PROXY',
    durationMs: number,
    priority: FetchPriority,
  ) {
    const lowCapPairs = data.pairs.filter((p: any) => {
      const mcap = p.marketCap || p.fdv || 0;
      return mcap === 0 || mcap <= 50000000;
    });

    const candidatePairs = lowCapPairs.length > 0 ? lowCapPairs : data.pairs;
    const sortedPairs = candidatePairs.sort((a: any, b: any) => {
      const scoreA = (a.liquidity?.usd || 0) + (a.volume?.h24 || 0);
      const scoreB = (b.liquidity?.usd || 0) + (b.volume?.h24 || 0);
      return scoreB - scoreA;
    });

    const top5 = sortedPairs.slice(0, 5);
    const bestPair = sortedPairs[0];
    const pairWithImage = data.pairs.find((p: any) => p.info?.imageUrl);
    const imageUrl = this.resolveImageUrl(bestPair, pairWithImage);

    const parsedCandidates: CandidateInfo[] = top5
      .filter((p: any) => p.baseToken?.address && p.chainId)
      .map((p: any) => ({
        mint: p.baseToken.address,
        chain: p.chainId,
        name: p.baseToken?.name,
        symbol: p.baseToken?.symbol,
        marketCap: p.marketCap || p.fdv,
        imageUrl: this.resolveImageUrl(p, pairWithImage),
        priceUsd: parseFloat(p.priceUsd || '0'),
        priceChange24h: p.priceChange?.h24,
        volume24h: p.volume?.h24,
        liquidity: p.liquidity?.usd,
        createdAt: p.pairCreatedAt,
        poolAddress: p.pairAddress,
      }));

    this.updateToken(tokenKey, {
      priceUsd: parseFloat(bestPair.priceUsd || '0'),
      priceChange24h: bestPair.priceChange?.h24,
      marketCap: bestPair.marketCap || bestPair.fdv,
      volume24h: bestPair.volume?.h24,
      liquidity: bestPair.liquidity?.usd,
      txns24h: bestPair.txns?.h24,
      createdAt: bestPair.pairCreatedAt,
      poolAddress: bestPair.pairAddress,
      websites: bestPair.info?.websites || pairWithImage?.info?.websites,
      socials: bestPair.info?.socials || pairWithImage?.info?.socials,
      name: bestPair.baseToken?.name,
      symbol: bestPair.baseToken?.symbol,
      imageUrl,
      chain: bestPair.chainId,
      mint: bestPair.baseToken?.address,
      candidatePairs: parsedCandidates,
      fetchedAt: Date.now(),
      hasFetchError: false,
      isLoading: false,
    });

    getWebSocketManager().subscribeToPair(bestPair);

    const mcapNum = bestPair.marketCap || bestPair.fdv || 0;
    const mcapStr = mcapNum >= 1000000 ? `$${(mcapNum / 1000000).toFixed(2)}M` : `$${(mcapNum / 1000).toFixed(1)}K`;
    const priceStr = `$${parseFloat(bestPair.priceUsd || '0').toFixed(6)}`;
    const liqNum = bestPair.liquidity?.usd || 0;
    const liqStr = liqNum >= 1000000 ? `$${(liqNum / 1000000).toFixed(2)}M` : `$${(liqNum / 1000).toFixed(1)}K`;
    const baseSymbol = bestPair.baseToken?.symbol || tokenKey;
    const quoteSymbol = bestPair.quoteToken?.symbol || 'USD';
    const pairName = `${baseSymbol}/${quoteSymbol} (${bestPair.dexId || 'DEX'}) | Chain: ${bestPair.chainId}`;

    recordApeLog({
      timestamp: new Date().toISOString(),
      tokenKey,
      route,
      status: 'SUCCESS',
      httpStatus: 200,
      durationMs,
      priority,
      activeCount: this.activeFetches,
      pendingCount: this.fetchQueue.length,
      pair: pairName,
      mcap: mcapStr,
      price: priceStr,
      liquidity: liqStr,
      detail: `${bestPair.baseToken?.symbol || tokenKey} on ${bestPair.chainId}`,
    });
  }

  private applyGmgnData(tokenKey: string, gmgn: any, chain: string, durationMs: number, priority: FetchPriority) {
    const priceUsd = parseFloat(gmgn.price?.price || '0');
    const price24hAgo = parseFloat(gmgn.price?.price_24h || '0');
    const priceChange24h = price24hAgo > 0 ? ((priceUsd - price24hAgo) / price24hAgo) * 100 : 0;

    let marketCap = 0;
    if (gmgn.circulating_supply && priceUsd > 0) {
      marketCap = parseFloat(gmgn.circulating_supply) * priceUsd;
    } else if (gmgn.total_supply && priceUsd > 0) {
      marketCap = parseFloat(gmgn.total_supply) * priceUsd;
    } else if (gmgn.pool?.base_reserve_value) {
      marketCap = parseFloat(gmgn.pool.base_reserve_value) * 2;
    } else if (gmgn.migration_market_cap) {
      marketCap = parseFloat(gmgn.migration_market_cap);
    }

    const websites = gmgn.link?.website ? [{ label: 'Website', url: gmgn.link.website }] : undefined;
    const socials: { type: string; url: string }[] = [];
    if (gmgn.link?.twitter_username) {
      socials.push({ type: 'twitter', url: `https://x.com/${gmgn.link.twitter_username}` });
    }
    if (gmgn.link?.telegram) {
      socials.push({ type: 'telegram', url: gmgn.link.telegram });
    }
    if (gmgn.link?.discord) {
      socials.push({ type: 'discord', url: gmgn.link.discord });
    }

    const txns24h = gmgn.price ? {
      buys: gmgn.price.buys_24h || 0,
      sells: gmgn.price.sells_24h || 0,
    } : undefined;

    this.updateToken(tokenKey, {
      priceUsd,
      priceChange24h,
      marketCap,
      volume24h: parseFloat(gmgn.price?.volume_24h || '0'),
      liquidity: parseFloat(gmgn.liquidity || gmgn.pool?.liquidity || '0'),
      txns24h,
      createdAt: gmgn.creation_timestamp ? gmgn.creation_timestamp * 1000 : undefined,
      websites,
      socials: socials.length > 0 ? socials : undefined,
      name: gmgn.name,
      symbol: gmgn.symbol,
      imageUrl: gmgn.logo,
      chain: chain === 'sol' ? 'solana' : 'ethereum',
      mint: gmgn.address || tokenKey,
      fetchedAt: Date.now(),
      hasFetchError: false,
      isLoading: false,
    });

    const mcapStr = marketCap >= 1000000
      ? `$${(marketCap / 1000000).toFixed(2)}M`
      : `$${(marketCap / 1000).toFixed(1)}K`;
    const priceStr = `$${priceUsd.toFixed(6)}`;
    const liqVal = parseFloat(gmgn.liquidity || gmgn.pool?.liquidity || '0');
    const liqStr = liqVal >= 1000000 ? `$${(liqVal / 1000000).toFixed(2)}M` : `$${(liqVal / 1000).toFixed(1)}K`;

    recordApeLog({
      timestamp: new Date().toISOString(),
      tokenKey,
      route: 'GMGN_FALLBACK',
      status: 'SUCCESS',
      httpStatus: 200,
      durationMs,
      priority,
      activeCount: this.activeFetches,
      pendingCount: this.fetchQueue.length,
      pair: `${gmgn.symbol || tokenKey}/USD (GMGN) | Chain: ${chain}`,
      mcap: mcapStr,
      price: priceStr,
      liquidity: liqStr,
      detail: `GMGN: ${gmgn.symbol} ($${priceStr}, MCAP: ${mcapStr})`,
    });
  }

  // Resolve the best available logo URL with fallback chain
  private resolveImageUrl(bestPair: any, pairWithImage: any): string | undefined {
    // 1. Try the DexScreener profile image (paid by token team)
    const profileImage = bestPair.info?.imageUrl || pairWithImage?.info?.imageUrl;
    if (profileImage) {
      return profileImage; // Direct request to bypass proxy blocks
    }

    // 2. Try the DexScreener auto-generated icon URL
    const chainId = bestPair.chainId;
    const address = bestPair.baseToken?.address;
    const chainSlug = chainId ? CHAIN_ICON_MAP[chainId] : undefined;
    if (chainSlug && address) {
      // CRITICAL FIX: DexScreener servers strictly require lowercase addresses for all EVM chains (ETH, Base, BSC).
      // Solana addresses are naturally case-sensitive and must NOT be lowercased.
      const formattedAddress = chainSlug === 'solana' ? address : address.toLowerCase();
      return `${DEXSCREENER_ICON_URL}/${chainSlug}/${formattedAddress}.png`;
    }

    return undefined;
  }

  // Fetch safety data (holders, snipers, dev, taxes, etc.) on demand when pill is expanded.
  // Uses the user's own IP by calling GMGN directly from the browser. Falls back to the
  // worker only if GMGN rate-limits this user's IP.
  async fetchSafetyData(tokenKey: string) {
    const data = this.cache.get(tokenKey);
    if (data?.safetyData) return; // Already fetched

    if (this.pendingSafetyRequests.has(tokenKey)) {
      return this.pendingSafetyRequests.get(tokenKey);
    }

    this.updateToken(tokenKey, { isSafetyLoading: true });

    const promise = (async () => {
      try {
        const address = data?.mint || tokenKey;
        const chain = data?.chain || (address.startsWith('0x') ? 'ethereum' : 'solana');
        // Default EVM addresses to 'eth' if the chain is unmapped (like 'robinhood')
        const chainSlug = CHAIN_SLUG_MAP[chain] || (address.startsWith('0x') ? 'eth' : 'sol');

        // eslint-disable-next-line no-console
        console.log(`[APE] fetchSafetyData → ${tokenKey} (chain=${chainSlug}, addr=${address})`);

        // 1. Try GMGN directly from browser (user's IP — independent rate limit per user)
        const [info, sec] = await Promise.all([
          gmgnDirectFetch('/v1/token/info', { chain: chainSlug, address }),
          gmgnDirectFetch('/v1/token/security', { chain: chainSlug, address }),
        ]);

        // eslint-disable-next-line no-console
        console.log(`[APE] GMGN raw info:`, info, '| raw sec:', sec);

        const parsed = parseSafetyFromGmgn(info, sec);

        // eslint-disable-next-line no-console
        console.log(`[APE] parseSafetyFromGmgn result:`, parsed);

        if (parsed && (parsed.holders > 0 || parsed.devAddress)) {
          // eslint-disable-next-line no-console
          console.log(`[APE] Safety: GMGN direct SUCCESS ✅ (holders=${parsed.holders}, dev=${parsed.devAddress})`);
          this.updateToken(tokenKey, { safetyData: parsed, isSafetyLoading: false });
          return;
        }

        // 2. Fallback: worker handles RugCheck/GoPlus (no browser CORS) + its own KV cache
        // eslint-disable-next-line no-console
        console.warn(`[APE] Safety: GMGN returned no usable data — falling back to WORKER (RugCheck/GoPlus)`);
        const workerUrl = `${WORKER_URL}/api/safety?chain=${chainSlug}&address=${address}`;
        // eslint-disable-next-line no-console
        console.log(`[APE] Worker fallback →`, workerUrl);
        const res = await fetch(workerUrl);
        // eslint-disable-next-line no-console
        console.log(`[APE] Worker /api/safety HTTP ${res.status}`);
        if (res.ok) {
          const safetyData: TokenSafetyData = await res.json();
          // eslint-disable-next-line no-console
          console.log(`[APE] Worker safety result:`, safetyData);
          this.updateToken(tokenKey, { safetyData, isSafetyLoading: false });
        } else {
          const errText = await res.text().catch(() => '(no body)');
          // eslint-disable-next-line no-console
          console.error(`[APE] Worker /api/safety failed HTTP ${res.status}:`, errText);
          this.updateToken(tokenKey, { isSafetyLoading: false });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[APE] fetchSafetyData unhandled error:`, err);
        this.updateToken(tokenKey, { isSafetyLoading: false });
      } finally {
        this.pendingSafetyRequests.delete(tokenKey);
      }
    })();

    this.pendingSafetyRequests.set(tokenKey, promise);
    return promise;
  }

  // Lazy Tier-2 loader: fetch developer predictions (Avg Peak, Avg Time) on scroll.
  // Calls GMGN created_tokens + klines directly from browser (user's IP). Falls back to
  // the worker only if GMGN rate-limits this user's IP.
  async fetchDevPrediction(tokenKey: string) {
    const data = this.cache.get(tokenKey);
    const devAddress = data?.safetyData?.devAddress;
    if (!devAddress) return;
    if (data.devPrediction) return; // Already fetched

    if (this.pendingPredictionRequests.has(tokenKey)) {
      return this.pendingPredictionRequests.get(tokenKey);
    }

    this.updateToken(tokenKey, { isDevPredictionLoading: true });

    const promise = (async () => {
      try {
        const address = data?.mint || tokenKey;
        const chain = data?.chain || (address.startsWith('0x') ? 'ethereum' : 'solana');
        const chainSlug = CHAIN_SLUG_MAP[chain] || (address.startsWith('0x') ? 'eth' : 'sol');

        // eslint-disable-next-line no-console
        console.log(`[APE] fetchDevPrediction → dev=${devAddress} chain=${chainSlug}`);

        // 1. Try GMGN directly from browser (user's IP)
        const directResult = await computeDevPrediction(chainSlug, devAddress);

        if (directResult) {
          // eslint-disable-next-line no-console
          console.log(`[APE] DevPrediction: GMGN direct SUCCESS ✅`, directResult);
          this.updateToken(tokenKey, { devPrediction: directResult, isDevPredictionLoading: false });
          return;
        }

        // 2. Fallback: worker (has its own KV cache for dev predictions)
        // eslint-disable-next-line no-console
        console.warn(`[APE] DevPrediction: GMGN direct returned null — falling back to WORKER`);
        const workerUrl = `${WORKER_URL}/api/dev-prediction?chain=${chainSlug}&dev=${devAddress}`;
        const res = await fetch(workerUrl);
        // eslint-disable-next-line no-console
        console.log(`[APE] Worker /api/dev-prediction HTTP ${res.status}`);
        if (res.ok) {
          const devPrediction: TokenDevPrediction = await res.json();
          // eslint-disable-next-line no-console
          console.log(`[APE] Worker dev-prediction result:`, devPrediction);
          this.updateToken(tokenKey, { devPrediction, isDevPredictionLoading: false });
        } else {
          const errText = await res.text().catch(() => '(no body)');
          // eslint-disable-next-line no-console
          console.error(`[APE] Worker /api/dev-prediction failed HTTP ${res.status}:`, errText);
          this.updateToken(tokenKey, { 
            devPrediction: { validTokens: 0, avgPeakMcap: 0, avgPeakMins: 0, avgTime: 0, strategy: 'Failed to load', totalCreated: 0, openRatio: 0 },
            isDevPredictionLoading: false 
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[APE] fetchDevPrediction unhandled error:`, err);
        this.updateToken(tokenKey, { 
          devPrediction: { validTokens: 0, avgPeakMcap: 0, avgPeakMins: 0, avgTime: 0, strategy: 'Failed to load', totalCreated: 0, openRatio: 0 },
          isDevPredictionLoading: false 
        });
      } finally {
        this.pendingPredictionRequests.delete(tokenKey);
      }
    })();

    this.pendingPredictionRequests.set(tokenKey, promise);
    return promise;
  }

  // Retry failed fetches after a delay
  private scheduleRetry(tokenKey: string, priority: FetchPriority = 'VISIBLE') {
    setTimeout(() => {
      // Only retry if we still have subscribers for this token
      if (this.subscribers.has(tokenKey)) {
        this.pendingRequests.delete(tokenKey);
        this.fetchTokenData(tokenKey, priority);
      }
    }, RETRY_DELAY_MS);
  }
}

const cryptoStore = new CryptoDataStore();
getCachedToken = (key: string) => cryptoStore['cache'].get(key);
export default cryptoStore;

export function useTokenData(tokenKey: string, priority: FetchPriority = 'VISIBLE'): TokenData {
  const [data, setData] = useState<TokenData>(() => cryptoStore['cache'].get(tokenKey) || { isLoading: true });

  useEffect(() => {
    return cryptoStore.subscribe(tokenKey, setData, priority);
  }, [tokenKey, priority]);

  return data;
}
